import type { JsonValue } from "type-fest";

// Runtime-sourced source system identity — the UPPERCASE `name` from the
// FusionHub `/integration-sources` endpoint. Branded so raw strings can't be
// assigned without crossing the API boundary via `asSourceSystemName`.
export type SourceSystemName = string & {
	readonly __brand: "SourceSystemName";
};
export const asSourceSystemName = (value: string): SourceSystemName =>
	value as SourceSystemName;

export const TAGS = [
	"Integrations",
	"SyncStatus",
	"SieFile",
	"VitecCredential",
	"VitecIntegrationConfig",
	"Job",
	"JobInIntegration",
	"SkatteverketOnboarding",
	"XledgerSyncSettings",
] as const;

export type FusionhubIntegration = {
	id: number;
	type: SourceSystemName;
	loadStatus: "INITIAL" | "LOADING" | "FAILED" | "READY";
	companyDomainId: string | null;
	createdAt: string;
	updatedAt: string;
	isPaused: boolean;
	pausedAt: string | null;
	pausedBy: string | null;
	pausedReason: string | null;
	values?: Record<string, string | number>;
};

// Mirrors FusionHub's integrationIssueResponseSchema. `type` is left as a plain
// string on purpose: FusionHub keeps adding `<source>_authentication_expired`
// members, and the portal matches on the suffix rather than a closed union.
export type IntegrationIssue = {
	id: number;
	type: string;
	data: Record<string, unknown>;
	detectedAt: string;
};

export type ErLegalEntity = {
	id: string;
	name: string;
	currencyBase: string;
};

export type ErSyncStatus =
	| {
			status: "FINISHED";
			finishedAt: Date;
	  }
	| {
			status: "FAILED";
			failedAt: Date;
	  }
	| {
			status: "IN_PROGRESS";
			startedAt: Date;
	  };

export type ErNote = {
	sv: string;
	en: string;
};

export type ErDocumentation = Record<
	string,
	{
		title: ErNote;
		notes: ErNote[];
		sources: Record<string, { notes: ErNote[] }>;
	}
>;

export type PaginatedResponse<T> = {
	items: T[];
	page: {
		cursor: string | null;
	};
};

export type PaginatedParams = {
	take?: number;
	cursor?: string;
	orderDirection?: "asc" | "desc";
};

export type MergeSystem = {
	name: string;
	slug: string;
	logo: string;
};

export const FUSION_HUB_JOB_STATUSES = [
	"PREPARING",
	"QUEUED",
	"RESERVED",
	"RUNNING",
	"COMPLETED",
	"FAILED",
	"TRANSIENT_FAILURE",
] as const;

export type FusionHubJobStatus = (typeof FUSION_HUB_JOB_STATUSES)[number];

type FusionHubConcurrencyStatus = "RUNNING" | "PENDING";

type FusionHubJobLogLevel =
	| "trace"
	| "debug"
	| "info"
	| "warn"
	| "error"
	| "fatal";

// Open keys (FusionHub#3626) — a closed vocabulary drops compound counts
// like createdOpportunities, which real sources (e.g. demo) already emit.
export type FusionHubSyncResultSummary = Record<string, number>;

// The step/status fields are populated by FusionHub's createStepLogger
// (FusionHub#3626) for sources wired onto it (currently DATEV, AFAS only) —
// absent for everything else, which is the "this source isn't wired up" signal.
export type FusionHubJobLogRecord = {
	level: FusionHubJobLogLevel;
	message: string;
	timestamp: string;
	metadata?: {
		step?: string;
		status?: "started" | "completed" | "failed" | "skipped";
		syncResult?: FusionHubSyncResultSummary;
		[key: string]: JsonValue | undefined;
	};
	error?: {
		message: string;
		name: string;
		stack: string;
		statusCode?: number;
		responseBody?: string;
	};
};

export type LiveTokenResponse = {
	url: string;
	expiresAt: string;
};

export type FusionHubJob = {
	id: number;
	name: string;
	status: FusionHubJobStatus;
	parameters: unknown;
	output: JsonValue;
	queuedAt: string;
	startedAt: string | null;
	completedAt: string | null;
	seenAt: string | null;
	concurrencyGroup: string | null;
	concurrencyStatus: FusionHubConcurrencyStatus | null;
	concurrencyCooldown: number | null;
	instanceId: number | null;
	integrationIds: number[];
	createdBy: string;
};
